import axios from "axios";
import AuthenticationService from "application/modules/authentication/AuthenticationService";
import variables from "globals/variables";

const application = axios.create({
  baseURL: variables.api.services,
});

const telemetry = axios.create({
  baseURL: variables.api.telemetry,
});

const appServices = axios.create({
  baseURL: variables.app.services,
});

const appTelemetry = axios.create({
  baseURL: variables.app.telemetry,
});

const mcx = axios.create({
  baseURL: variables.api.mcx,
});

const externalCallServer = axios.create({
  baseURL: `${AuthenticationService.getRLUIpAddress()}`,
});

const requestHandler = (request) => {
  const token = AuthenticationService.getAuthenticationToken();
  request.headers.Authorization = `Bearer ${token}`;
  if (!token) {
    console.error(`[AUTH-TRACE] REQUEST with NULL token: ${request.method?.toUpperCase()} ${request.baseURL}${request.url}`);
  }
  return request;
};

const responseHandler = (response) => {
  return response;
};

const errorHandler = (error) => {
  const url = error.config?.url || "unknown";
  const base = error.config?.baseURL || "";
  const status = error.response?.status;
  const msg = error.response?.data?.message || error.message;
  const authHeader = error.config?.headers?.Authorization || "none";
  const authType = authHeader.split(" ")[0];

  if (status === 401) {
    console.error(`[AUTH-TRACE] 401 from ${base}${url} | auth: ${authType} | msg: ${msg}`);
    const originalRequest = error.config;
    if (!originalRequest._retry) {
      originalRequest._retry = true;
      console.warn(`[AUTH-TRACE] Attempting token refresh after 401 on ${url}`);
      return refreshTokenRequest().then(() => {
        console.warn(`[AUTH-TRACE] Refresh succeeded, retrying ${url}`);
        return axios(originalRequest);
      });
    } else {
      console.error(`[AUTH-TRACE] LOGOUT: retry also got 401 on ${url} — redirecting to /site/log-out`);
      window.location = "/site/log-out";
    }
  } else if (status === 403) {
    console.error(`[AUTH-TRACE] LOGOUT: 403 from ${base}${url} | auth: ${authType} | msg: ${msg} — redirecting to /site/log-out`);
    window.location = "/site/log-out";
  }
  return Promise.reject(error);
};

const refreshTokenRequest = () => {
  const refreshToken = AuthenticationService.getRefreshToken();
  console.warn(`[AUTH-TRACE] refreshTokenRequest called | refreshToken present: ${!!refreshToken} | length: ${refreshToken?.length || 0}`);
  return axios
    .post(`${variables.api.services}user/refreshToken`, null, {
      headers: {
        Authorization: `Refresh-Bearer ${refreshToken}`,
      },
    })
    .then((response) => {
      console.warn(`[AUTH-TRACE] refreshToken succeeded — new token stored`);
      AuthenticationService.setRefreshedToken(
        response.data.data.token,
        response.data.data.refreshToken
      );
      return Promise.resolve();
    })
    .catch((error) => {
      console.error(`[AUTH-TRACE] LOGOUT: refreshToken failed — ${error.response?.status} ${error.response?.data?.message || error.message} — redirecting to /site/log-out`);
      window.location = "/site/log-out";
    });
};

application.interceptors.request.use(
  (request) => requestHandler(request),
  (error) => errorHandler(error)
);

application.interceptors.response.use(
  (response) => responseHandler(response),
  (error) => errorHandler(error)
);

externalCallServer.interceptors.request.use(
  (request) => requestHandler(request),
  (error) => errorHandler(error)
);

externalCallServer.interceptors.response.use(
  (response) => responseHandler(response),
  (error) => errorHandler(error)
);

appServices.interceptors.request.use(
  (request) => requestHandler(request),
  (error) => errorHandler(error)
);

appServices.interceptors.response.use(
  (response) => responseHandler(response),
  (error) => errorHandler(error)
);

appTelemetry.interceptors.request.use(
  (request) => requestHandler(request),
  (error) => errorHandler(error)
);

appTelemetry.interceptors.response.use(
  (response) => responseHandler(response),
  (error) => errorHandler(error)
);

telemetry.interceptors.request.use(
  (request) => requestHandler(request),
  (error) => errorHandler(error)
);

telemetry.interceptors.response.use(
  (response) => responseHandler(response),
  (error) => errorHandler(error)
);

mcx.interceptors.request.use(
  (request) => requestHandler(request), 
  (error) => errorHandler(error)
);

mcx.interceptors.response.use(
  (response) => responseHandler(response), 
  (error) => errorHandler(error)
);

const generateServicesEndPoint = (urlScheme, ipAddress) => {
  let baseURL = `${urlScheme}//${ipAddress}/services/api/v2/`;
  const endPoint = axios.create({
    baseURL: baseURL,
  });

  endPoint.interceptors.request.use(
    (request) => requestHandler(request),
    (error) => errorHandler(error)
  );

  endPoint.interceptors.response.use(
    (response) => responseHandler(response),
    (error) => errorHandler(error)
  );

  return endPoint;
};

const generateTelemetryEndPoint = (urlScheme, ipAddress) => {
  const endPoint = axios.create({
    baseURL: `${urlScheme}//${ipAddress}/telemetry/api/v2/`,
  });

  endPoint.interceptors.request.use(
    (request) => requestHandler(request),
    (error) => errorHandler(error)
  );

  endPoint.interceptors.response.use(
    (response) => responseHandler(response),
    (error) => errorHandler(error)
  );

  return endPoint;
};

export {
  application,
  telemetry,
  mcx,
  externalCallServer,
  appServices,
  appTelemetry,
  generateServicesEndPoint,
  generateTelemetryEndPoint,
};
